import React, { useEffect } from "react"

import { Table2 } from "lucide-react"

import { useTableWidgetData } from "../hooks/useTableWidgetData"
import type { ChartClickData } from "../types/chart"
import type { GroupByType } from "../types/chart-types"
import {
  AGGREGATION_TYPE,
  DATA_SOURCE,
  type FilterType,
  type MetricFilter,
} from "../types/core"
import {
  BaseWidget,
  type MetricWidgetConfig,
  type WidgetExportData,
  type WidgetProps,
} from "./BaseWidget"
import { TableWidgetTable } from "./TableWidgetTable"

interface TableWidgetProps extends WidgetProps {
  onExportDataReady?: (data: WidgetExportData | null) => void
  onCellClick?: (
    clickData: ChartClickData,
    conversationsHref: string | null,
  ) => void
}

interface TableWidgetBodyProps {
  config: MetricWidgetConfig & {
    type: "table"
    filters?: FilterType
    groupBy?: GroupByType
    groupByMetadataKey?: string
    metricFilter?: MetricFilter[]
  }
  fallbackIcon: React.ReactNode
  fallbackText: string
  onExportDataReady?: (data: WidgetExportData | null) => void
  onCellClick?: (
    clickData: ChartClickData,
    conversationsHref: string | null,
  ) => void
}

const TableWidgetBody: React.FC<TableWidgetBodyProps> = ({
  config,
  fallbackIcon,
  fallbackText,
  onExportDataReady,
  onCellClick,
}) => {
  const data = useTableWidgetData({ config })

  useEffect(() => {
    if (!data.hasMetrics) {
      onExportDataReady?.(null)
    }
  }, [data.hasMetrics, onExportDataReady])

  if (!data.hasMetrics) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        {fallbackIcon}
        <p className="text-sm">{fallbackText}</p>
      </div>
    )
  }

  return (
    <TableWidgetTable
      data={data}
      config={config}
      onExportDataReady={onExportDataReady}
      onCellClick={onCellClick}
    />
  )
}

export class TableWidget extends BaseWidget {
  type = "table"
  title = "Table"
  description = "Display data in a table format"
  icon = (<Table2 className="w-5 h-5" />)
  disabled = false

  renderWidget(props: TableWidgetProps): React.ReactNode {
    const config = props.widget.config
    const resolvedConfig = {
      ...(config || this.getDefaultConfig()),
      type: "table" as const,
    }

    return (
      <TableWidgetBody
        config={resolvedConfig}
        fallbackIcon={<Table2 className="w-8 h-8 mx-auto mb-2" />}
        fallbackText="Select metrics and test sets to display data"
        onExportDataReady={props.onExportDataReady}
        onCellClick={props.onCellClick}
      />
    )
  }

  getDefaultConfig(): MetricWidgetConfig {
    return {
      title: this.title,
      description: "",
      metricId: "",
      dataSource: DATA_SOURCE.SIMULATED,
      aggregation: AGGREGATION_TYPE.SUCCESS,
    }
  }
}
